import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

// components
import BasicButton from '../../components/buttons/basicButton';
import PageBasic from '../../components/display/layout/pageBasic';
import AampeEmptyStateV2 from '../../components/emptyStates/AampeEmptyStateV2';
import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';
// constants
import routes from '../../constants/routes';

// hooks
import useFormulasCategoryCountQuery from '../../hooks/formulas/useFormulasCategoryCount';
import useAnalytics from '../../hooks/helper/useAnalytics';
import useGetLocationState from '../../hooks/helper/useGetLocationState';
import useUserStatusQuery from '../../hooks/userStatus/useUserStatusQuery';
import useRoleFlags from 'hooks/auth/useRoleFlags';

import FormulaCatalogActions from './FormulaCatalogActions';
import FormulasCatalogStatus from './FormulasCatalogStatus';

const FormulasCatalogMain: React.FC<React.PropsWithChildren<unknown>> = () => {
    const history = useHistory();
    const location = history.location;
    const { getLocationState } = useGetLocationState('Back to messages');

    const { data, isPending: isLoading } = useUserStatusQuery();
    const { isLoading: categoryCountIsLoading } =
        useFormulasCategoryCountQuery();

    const { track } = useAnalytics();

    const emptyState = useMemo(() => !data?.formula_created, [data]);

    const { hasDeleteAccess, hasCreateFormulaAccess } = useRoleFlags();

    const handleCreateMessageClick = () => {
        track('create_formula_button_clicked');
        history.push(routes.formulasCreate, getLocationState());
    };

    const getPageTitle = useMemo(() => {
        return location.pathname.replace(`${routes.formulasMain}/`, '');
    }, [location.pathname]);

    return (
        <PageBasic
            title={getPageTitle}
            actionBar={
                !emptyState && (
                    <FormulaCatalogActions
                        handleCreateMessageClick={handleCreateMessageClick}
                    />
                )
            }
            isLoading={isLoading || categoryCountIsLoading}
        >
            {emptyState ? (
                <FormulasEmptyState
                    header={'Build out your messages'}
                    body={
                        <EmptyStateBody>
                            <EmptyStateBodyText>
                                Writing a message in Aampe's editor is a piece
                                of cake.{' '}
                                <span role="img" aria-label="cake">
                                    🍰
                                </span>
                            </EmptyStateBodyText>
                            <BasicButton
                                name={'Try the message editor'}
                                action={handleCreateMessageClick}
                            />
                        </EmptyStateBody>
                    }
                    imageUrl={
                        'https://storage.googleapis.com/assets.aampe.com/app_images/empty_state_placeholders/formulas.svg'
                    }
                />
            ) : (
                <FormulasCatalogStatus
                    hasDeleteAccess={hasDeleteAccess}
                    hasCreateFormulaAccess={hasCreateFormulaAccess}
                />
            )}
        </PageBasic>
    );
};

const FormulasEmptyState = styled(AampeEmptyStateV2)({
    paddingBottom: aampeTheme.spacing(5),
});

const EmptyStateBody = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(2),
    alignItems: 'center',
});

const EmptyStateBodyText = styled.p({
    fontSize: '14px',
    color: aampeTheme.colors.gray700,
});

export default FormulasCatalogMain;
